import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, CircularProgress, Stack, debounce } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { ContentWrapper } from '@/components/ContentWrapper';
import {
    FONT_WEIGHT,
    Heading,
    LINE_HEIGHT,
    PageSubHeading,
    SMBold,
    SMRegular,
    SMSemiBold,
    TextMDSemiBold,
    XSMedium
} from '@/typography/Typography';
import { TableCard } from '@/components/shared/Card';
import CardHeader from '@/components/shared/CardHeader';
import Chip from '@/components/shared/Chip';
import { InputText } from '@/components/shared/InputText';
import { Dropdown, DropDownChoices } from '@/components/shared/Dropdown';
import { useUser } from '@/contexts/user/UserContext';
import { UserRoles } from '@/models/company';
import { useAdminCompanies, useAppDispatch } from '@/lib/hooks';
import { getAdminCompanies } from '@/lib/slices/paymentsSlice';
import { DATE_FORMAT } from '@/lib/constants';
import {
    useCampaignActivationFunnelQuery,
    useCampaignFunnelCampaignsQuery
} from '@/services/reportsQuery';
import { CampaignFunnelReport } from '@/services/CampaignActivationFunnelTypes';

const DEFAULT_ATTRIBUTION_WINDOW_DAYS = 7;

/**
 * Non-empty on purpose: Dropdown treats an empty-string value as "nothing selected" and
 * renders its placeholder instead, so an `''` sentinel could never display as "Any".
 */
const ANY_COMPANY_VALUE = 'any';

/** The dropdown shows only the start of a campaign name; search still spans the whole one. */
const CAMPAIGN_NAME_DISPLAY_LENGTH = 20;

const WINDOW_DEBOUNCE_MS = 400;

const isPositiveWholeNumber = (raw: string) => {
    const parsed = Number(raw);
    return Number.isInteger(parsed) && parsed > 0;
};

// flex-start, not flex-end: the attribution window always carries helper text, so
// bottom-aligning the columns would push its input box above the other two.
const FilterBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.greySurface,
    borderRadius: '8px',
    border: `1px solid ${theme.palette.greyBorder}`,
    margin: theme.spacing(1)
}));

const FunnelRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1, 0)
}));

const FunnelLabel = styled(Box)(() => ({
    minWidth: 140,
    flexShrink: 0
}));

const FunnelTrack = styled(Box)(({ theme }) => ({
    flex: 1,
    height: 28,
    borderRadius: '5px',
    backgroundColor: theme.palette.greySurface,
    border: `1px solid ${theme.palette.greyBorder}`,
    overflow: 'hidden'
}));

// Filled portion of a funnel bar. Width is set inline per row (the only
// data-driven dimension); the colour is a theme token, never hardcoded.
const FunnelFill = styled(Box)(({ theme }) => ({
    height: '100%',
    backgroundColor: theme.palette.primary.main,
    transition: 'width 200ms ease'
}));

const FunnelCount = styled(Box)(() => ({
    minWidth: 150,
    flexShrink: 0,
    textAlign: 'right'
}));

interface FunnelStage {
    labelKey: string;
    value: number;
}

export const CampaignActivationFunnelReport = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { user } = useUser();
    const { companies } = useAdminCompanies();

    const isSuperAdmin = user?.role === UserRoles.SuperAdmin;

    useEffect(() => {
        if (!isSuperAdmin) {
            navigate('/', { replace: true });
        }
    }, [isSuperAdmin, navigate]);

    useEffect(() => {
        dispatch(getAdminCompanies());
    }, [dispatch]);

    const [companySelection, setCompanySelection] =
        useState<string>(ANY_COMPANY_VALUE);
    const [campaignId, setCampaignId] = useState<string>('');
    const [windowInput, setWindowInput] = useState<string>(
        String(DEFAULT_ATTRIBUTION_WINDOW_DAYS)
    );
    // Only the window is debounced: it is typed digit by digit, so "1" → "10" → "100"
    // would otherwise fire three requests. The two dropdowns commit in one click.
    const [debouncedWindowInput, setDebouncedWindowInput] = useState<string>(
        String(DEFAULT_ATTRIBUTION_WINDOW_DAYS)
    );

    const commitWindowInput = useMemo(
        () => debounce(setDebouncedWindowInput, WINDOW_DEBOUNCE_MS),
        []
    );

    useEffect(() => () => commitWindowInput.clear(), [commitWindowInput]);

    const companyId =
        companySelection === ANY_COMPANY_VALUE ? undefined : companySelection;

    const {
        data: campaigns,
        isFetching: isFetchingCampaigns,
        isError: isCampaignsError
    } = useCampaignFunnelCampaignsQuery(companyId, isSuperAdmin);

    // A campaign the newly selected company never received must not survive the switch: the funnel
    // would otherwise keep reporting the previous scope's campaign against a company it never
    // reached, which reads as all-zero bars rather than as a mismatch. Deliberately gated on a
    // settled list — placeholderData serves the previous company's campaigns during a refetch.
    useEffect(() => {
        if (isFetchingCampaigns || !campaigns) return;

        if (campaignId && !campaigns.some((c) => c.campaignId === campaignId)) {
            setCampaignId('');
        }
    }, [campaigns, campaignId, isFetchingCampaigns]);

    const companyChoices = useMemo<DropDownChoices<string>[]>(
        () => [
            {
                label: t('campaign_activation_funnel.any_company'),
                value: ANY_COMPANY_VALUE
            },
            ...(companies ?? [])
                .map((c) => ({ label: c.name, value: c.companyId }))
                .sort((a, b) => a.label.localeCompare(b.label))
        ],
        [companies, t]
    );

    // Never re-sorted: the backend's ascending-numeric id order is the required one, and
    // is not what sorting these display labels alphabetically would produce.
    const campaignChoices = useMemo<DropDownChoices<string>[]>(
        () =>
            (campaigns ?? []).map(({ campaignId: id, campaignName }) => {
                const name = campaignName ?? '';
                return {
                    label: `${id} ${name.slice(
                        0,
                        CAMPAIGN_NAME_DISPLAY_LENGTH
                    )}`.trim(),
                    searchValue: `${id} ${name}`.trim(),
                    value: id
                };
            }),
        [campaigns]
    );

    const windowDays = Number(debouncedWindowInput);
    const isWindowValid = isPositiveWholeNumber(debouncedWindowInput);
    // Validated against the raw input so the message appears while typing, not a beat later.
    const showWindowError =
        !!windowInput && !isPositiveWholeNumber(windowInput);

    // The selector is scoped to the company, so an empty list means the company never received a
    // campaign at all — a different message from "pick one". Only settled once the list resolves.
    const hasNoCampaigns =
        !isFetchingCampaigns &&
        !isCampaignsError &&
        (campaigns?.length ?? 0) === 0;

    const { data, isFetching, isError } = useCampaignActivationFunnelQuery(
        campaignId,
        companyId,
        windowDays,
        !!campaignId && isWindowValid
    );

    if (!isSuperAdmin) return null;

    return (
        <ContentWrapper>
            <>
                <Box m={1}>
                    <Heading
                        fontSize={20}
                        fontWeight={FONT_WEIGHT.MEDIUM}
                        lineHeight={LINE_HEIGHT.XLARGE}
                    >
                        {t('campaign_activation_funnel.title')}
                    </Heading>
                    <PageSubHeading>
                        {t('campaign_activation_funnel.subtitle')}
                    </PageSubHeading>
                </Box>

                <FilterBox>
                    <Box minWidth={240}>
                        {/* InputText's own label renders at LINE_HEIGHT.MEDIUM_SMALL (its
                            body1 variant's 1.5x line-height on a 14px font) with no exposed
                            override, so this label is pinned to match rather than the
                            reverse — otherwise flex-start still leaves the Attribution
                            Window's input a few px lower than this Dropdown's. */}
                        <SMBold
                            style={{ lineHeight: LINE_HEIGHT.MEDIUM_SMALL }}
                        >
                            {t('campaign_activation_funnel.company')}
                        </SMBold>
                        <Dropdown
                            label={t(
                                'campaign_activation_funnel.select_company'
                            )}
                            choices={companyChoices}
                            value={companySelection}
                            onChange={(value: string) =>
                                setCompanySelection(value ?? ANY_COMPANY_VALUE)
                            }
                        />
                    </Box>
                    <Box minWidth={280}>
                        <SMBold
                            style={{ lineHeight: LINE_HEIGHT.MEDIUM_SMALL }}
                        >
                            {t('campaign_activation_funnel.campaign_id')}
                        </SMBold>
                        {/* Empty string, not null, when nothing is selected: Dropdown only resets its
                            internal label on a zero-length value, so a null would leave the cleared
                            campaign still showing after the company switch clears it programmatically. */}
                        <Dropdown
                            label={t(
                                'campaign_activation_funnel.select_campaign'
                            )}
                            choices={campaignChoices}
                            value={campaignId}
                            onChange={(value: string) =>
                                setCampaignId(value ?? '')
                            }
                            isDisabled={isFetchingCampaigns || hasNoCampaigns}
                            error={isCampaignsError}
                            searchable
                            allowClear
                        />
                        <XSMedium color="text.secondary">
                            {t(
                                'campaign_activation_funnel.campaign_clear_hint'
                            )}
                        </XSMedium>
                    </Box>
                    <Box minWidth={200}>
                        <InputText
                            type="number"
                            label={t(
                                'campaign_activation_funnel.attribution_window'
                            )}
                            value={windowInput}
                            onChange={(e) => {
                                setWindowInput(e.target.value);
                                commitWindowInput(e.target.value);
                            }}
                            error={showWindowError}
                            helperText={
                                showWindowError
                                    ? t(
                                          'campaign_activation_funnel.attribution_window_invalid'
                                      )
                                    : t(
                                          'campaign_activation_funnel.attribution_window_hint'
                                      )
                            }
                        />
                    </Box>
                </FilterBox>

                {isCampaignsError && (
                    <Box px={2} pb={2}>
                        <Alert severity="error">
                            {t('campaign_activation_funnel.campaigns_error')}
                        </Alert>
                    </Box>
                )}

                {/* The funnel is anchored to one campaign's send time, so there is no meaningful
                    all-campaigns aggregate to fall back to — no selection is an explicit prompt
                    rather than blank space below the filters. */}
                {!campaignId && !isCampaignsError && (
                    <Box px={3} py={4} m={1}>
                        <SMRegular color="text.secondary">
                            {hasNoCampaigns
                                ? t(
                                      'campaign_activation_funnel.no_campaigns_for_company'
                                  )
                                : t(
                                      'campaign_activation_funnel.select_campaign_prompt'
                                  )}
                        </SMRegular>
                    </Box>
                )}

                {!!campaignId && (
                    <TableCard>
                        <CardHeader
                            label={
                                data?.campaignName ??
                                t('campaign_activation_funnel.title')
                            }
                            description={t(
                                'campaign_activation_funnel.results_for_campaign'
                            )}
                            chip={
                                data && data.channels.length > 0 ? (
                                    <Chip
                                        color="info"
                                        label={data.channels.join(' · ')}
                                    />
                                ) : undefined
                            }
                        />

                        {isFetching && (
                            <Box display="flex" justifyContent="center" py={6}>
                                <CircularProgress size={32} />
                            </Box>
                        )}

                        {!isFetching && isError && (
                            <Box px={2} pb={2}>
                                <Alert severity="error">
                                    {t('campaign_activation_funnel.error')}
                                </Alert>
                            </Box>
                        )}

                        {/* A report with no recipients at all is a scope mismatch, not a funnel of
                            zeros — the backend still returns a well-formed report, so six empty
                            bars would otherwise read as a real (and alarming) result. */}
                        {!isFetching &&
                            !isError &&
                            (!data || data.totalRecipients === 0) && (
                                <Box px={2} py={4}>
                                    <SMRegular color="text.secondary">
                                        {companyId
                                            ? t(
                                                  'campaign_activation_funnel.no_data_for_company'
                                              )
                                            : t(
                                                  'campaign_activation_funnel.no_data'
                                              )}
                                    </SMRegular>
                                </Box>
                            )}

                        {!isFetching &&
                            !isError &&
                            data &&
                            data.totalRecipients > 0 && (
                                <FunnelView report={data} />
                            )}
                    </TableCard>
                )}
            </>
        </ContentWrapper>
    );
};

interface FunnelViewProps {
    report: CampaignFunnelReport;
}

const FunnelView = ({ report }: FunnelViewProps) => {
    const { t } = useTranslation();

    const stages: FunnelStage[] = [
        {
            labelKey: 'campaign_activation_funnel.stage_sent',
            value: report.sent
        },
        {
            labelKey: 'campaign_activation_funnel.stage_delivered',
            value: report.delivered
        },
        {
            labelKey: 'campaign_activation_funnel.stage_opened',
            value: report.opened
        },
        {
            labelKey: 'campaign_activation_funnel.stage_clicked',
            value: report.clicked
        },
        {
            labelKey: 'campaign_activation_funnel.stage_first_login',
            value: report.firstLogin
        },
        {
            labelKey: 'campaign_activation_funnel.stage_first_withdrawal',
            value: report.firstWithdrawal
        }
    ];

    // Bars are proportional to the top of the funnel (Sent), the largest stage by
    // construction (monotonic). Fall back to the max stage value so a zero-Sent
    // funnel still renders sensible bar widths.
    const denominator =
        Math.max(report.sent, ...stages.map((s) => s.value)) || 1;

    return (
        <Box px={2} pb={3}>
            <Box pb={2}>
                <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                >
                    <Chip
                        color="success"
                        label={t(
                            'campaign_activation_funnel.matched_coverage',
                            {
                                matched: report.matchedRecipients,
                                total: report.totalRecipients
                            }
                        )}
                    />
                    <SMRegular color="text.secondary">
                        {t('campaign_activation_funnel.matched_coverage_hint')}
                    </SMRegular>
                </Stack>
                <Box pt={1}>
                    <SMRegular color="text.secondary">
                        {t('campaign_activation_funnel.window_label', {
                            days: report.attributionWindowDays
                        })}
                    </SMRegular>
                </Box>
                <Box>
                    {report.campaignSendTime ? (
                        <SMRegular color="text.secondary">
                            {t('campaign_activation_funnel.send_time_label', {
                                date: dayjs(report.campaignSendTime).format(
                                    DATE_FORMAT.FULL_DATE_TIME
                                )
                            })}
                        </SMRegular>
                    ) : (
                        <SMRegular color="text.secondary">
                            {t('campaign_activation_funnel.send_time_unknown')}
                        </SMRegular>
                    )}
                </Box>
            </Box>

            {stages.map((stage) => (
                <FunnelRow key={stage.labelKey}>
                    <FunnelLabel>
                        <SMSemiBold>{t(stage.labelKey)}</SMSemiBold>
                    </FunnelLabel>
                    <FunnelTrack>
                        <FunnelFill
                            sx={{
                                width: `${(stage.value / denominator) * 100}%`
                            }}
                        />
                    </FunnelTrack>
                    <FunnelCount>
                        <TextMDSemiBold>{stage.value}</TextMDSemiBold>
                        <SMRegular color="text.secondary">
                            {t(
                                'campaign_activation_funnel.stage_count_of_matched',
                                {
                                    count: stage.value,
                                    matched: report.matchedRecipients
                                }
                            )}
                        </SMRegular>
                    </FunnelCount>
                </FunnelRow>
            ))}

            <Box pt={2}>
                <SMRegular color="text.secondary">
                    {t('campaign_activation_funnel.activation_window_note')}
                </SMRegular>
            </Box>
        </Box>
    );
};
